import clsx from "clsx";

import { LongFormPollResponses } from "./long_form_poll_responses";
import { PollChart } from "./poll_chart";
import { PollChoices } from "./poll_choices";

import { NonLongFormPollResponseForm } from "components/stage/poll_session_pane/non_long_form_poll_response_form";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useUsersProfileProperties } from "hooks/use_user_properties";
import { setPollSessionViewResultsAs } from "shared/reducers/classroom";
import {
  FeaturedPollResponseType,
  PollSessionStatusType,
  PollSessionType,
  PollSessionViewResultsAsType,
  PollType,
  PollTypeType,
} from "shared/types";
import { classroomSelectors } from "store";

function PollResponses({
  poll,
  pollSession,
  canControlClassroom,
  featuredPollResponse,
  canSeeAllPollResponses,
  userId,
}: {
  poll: PollType;
  pollSession: PollSessionType;
  canControlClassroom: boolean;
  featuredPollResponse?: FeaturedPollResponseType;
  canSeeAllPollResponses: boolean;
  userId: string;
}) {
  const dispatch = useAppDispatch();
  const users = useUsersProfileProperties();
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const pollResponses = useArraySelector(
    classroomSelectors.selectPollResponsesByPollSessionId(pollSession.id),
  );
  const canFeaturePollResponse =
    canControlClassroom &&
    pollSession.status === PollSessionStatusType.Completed &&
    !isPresentationView;
  const isLongFormPoll = poll.type === PollTypeType.LongForm;

  const handleChangeViewResultsAs = (viewResultsAs: PollSessionViewResultsAsType) => () => {
    dispatch(setPollSessionViewResultsAs({ id: pollSession.id, viewResultsAs }));
  };

  return isLongFormPoll ? (
    <LongFormPollResponses
      pollSessionId={pollSession.id}
      pollResponses={pollResponses}
      users={users}
      canFeaturePollResponse={canFeaturePollResponse}
      featuredPollResponse={featuredPollResponse}
      canSeeAllPollResponses={canSeeAllPollResponses}
      isPresentationView={isPresentationView}
    />
  ) : (
    <>
      {canControlClassroom && !isPresentationView && (
        <div className="fds-tabs mb5">
          <button
            type="button"
            className={clsx("tab", {
              "tab--active": pollSession.view_results_as === PollSessionViewResultsAsType.Pie,
            })}
            onClick={handleChangeViewResultsAs(PollSessionViewResultsAsType.Pie)}
          >
            Pie Chart
          </button>
          <button
            type="button"
            className={clsx("tab", {
              "tab--active": pollSession.view_results_as === PollSessionViewResultsAsType.Bar,
            })}
            onClick={handleChangeViewResultsAs(PollSessionViewResultsAsType.Bar)}
          >
            Bar Chart
          </button>
          <button
            type="button"
            className={clsx("tab", {
              "tab--active": pollSession.view_results_as === PollSessionViewResultsAsType.Choices,
            })}
            onClick={handleChangeViewResultsAs(PollSessionViewResultsAsType.Choices)}
          >
            Options
          </button>
        </div>
      )}
      {canSeeAllPollResponses ? (
        (() => {
          switch (pollSession.view_results_as) {
            case PollSessionViewResultsAsType.Pie:
            case PollSessionViewResultsAsType.Bar:
              return (
                <PollChart
                  poll={poll}
                  pollSession={pollSession}
                  pollResponses={pollResponses}
                  users={users}
                />
              );
            case PollSessionViewResultsAsType.Choices:
              // The `NonLongFormPollResponseForm` will show a student's poll response (if submitted).
              return canControlClassroom ? (
                <PollChoices poll={poll} />
              ) : (
                <NonLongFormPollResponseForm
                  poll={poll}
                  pollSession={pollSession}
                  userId={userId}
                  readOnly
                />
              );
            default:
              return null;
          }
        })()
      ) : isPresentationView ? (
        <PollChoices poll={poll} />
      ) : (
        <NonLongFormPollResponseForm
          poll={poll}
          pollSession={pollSession}
          userId={userId}
          readOnly
        />
      )}
    </>
  );
}

export { PollResponses };
